<script lang="ts">
	import { editable } from '$lib/storyblok/editable';
	import type { ISbStoryData } from '@storyblok/js';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import Blok from '../blok.svelte';
	import type { CardCategory } from './wallOfLove/types';
	import Button from '$lib/buttons/v3/button.svelte';
	import { browser } from '$app/environment';
	import { tick } from 'svelte';
	import { slide } from 'svelte/transition';

	interface Props {
		content: ISbStoryData<{
			mobile_items: Array<
				ISbStoryData<{ category: CardCategory; delay?: number; threshold?: number }>['content']
			>;
			desktop_items: Array<
				ISbStoryData<{ category: CardCategory; delay?: number; threshold?: number }>['content']
			>;
		}>['content'];
	}

	let { content }: Props = $props();

	type ActiveCategory = CardCategory | 'all';

	const categoryNames: Record<ActiveCategory, string> = {
		all: 'All',
		media: 'Media',
		influencer: 'Influencer',
		reviews: 'Reviews',
		chefs: 'Chefs'
	};

	let availableMobileCategories = $derived([
		'all',
		...new Set(content.mobile_items.map((item) => item.category))
	]);

	let availableDesktopCategories = $derived([
		'all',
		...new Set(content.desktop_items.map((item) => item.category))
	]);

	let activeMobileCategory = $state<ActiveCategory>('all');
	let activeDesktopCategory = $state<ActiveCategory>('all');

	let filteredMobileItems = $derived(
		activeMobileCategory === 'all'
			? content.mobile_items
			: content.mobile_items.filter((item) => item.category === activeMobileCategory)
	);

	let filteredDesktopItems = $derived(
		activeDesktopCategory === 'all'
			? content.desktop_items
			: content.desktop_items.filter((item) => item.category === activeDesktopCategory)
	);

	let containerDiv: HTMLDivElement | undefined = $state();
	let contentSection: HTMLElement | undefined = $state();
	let currentHeightVh = $state(75);
	let allContentVisible = $state(true);

	function loadMore() {
		if (!contentSection || !containerDiv) return;

		const contentScrollHeight = contentSection.scrollHeight;
		const currentContainerHeightPx = containerDiv.offsetHeight;

		if (currentContainerHeightPx < contentScrollHeight) {
			const svhInPixels = window.innerHeight * 0.75;
			const newContainerHeightPx = currentContainerHeightPx + svhInPixels;
			const remainingHiddenPx = contentScrollHeight - newContainerHeightPx;
			const remainingHiddenSvh = (remainingHiddenPx / window.innerHeight) * 100;

			if (
				newContainerHeightPx >= contentScrollHeight ||
				(remainingHiddenPx > 0 && remainingHiddenSvh < 20)
			) {
				currentHeightVh = (contentScrollHeight / window.innerHeight) * 100;
				allContentVisible = true;
			} else {
				currentHeightVh += 75;
			}
		} else {
			allContentVisible = true;
		}
	}

	// Initial check when component mounts and elements are available
	$effect(() => {
		if (browser && contentSection && containerDiv) {
			// Initial check in case content is already shorter than 75svh
			// Run after a tick to ensure DOM is settled if content loads async with component
			tick().then(() => {
				if (contentSection && containerDiv) {
					const currentContainerHeightPx = containerDiv.offsetHeight;
					const contentScrollHeight = contentSection.scrollHeight;

					if (currentContainerHeightPx < contentScrollHeight) {
						const remainingHiddenPx = contentScrollHeight - currentContainerHeightPx;
						const remainingHiddenSvh = (remainingHiddenPx / window.innerHeight) * 100;

						if (remainingHiddenPx > 0 && remainingHiddenSvh < 20) {
							currentHeightVh = (contentScrollHeight / window.innerHeight) * 100;
							allContentVisible = true;
						} else {
							allContentVisible = false;
						}
					} else {
						allContentVisible = true;
					}
				}
			});
		}
	});

	// Effect for category changes
	$effect(() => {
		// Ensure effect re-runs on category change by accessing them
		activeMobileCategory;
		activeDesktopCategory;

		if (browser && contentSection && containerDiv) {
			// Reset height and visibility when categories change
			currentHeightVh = 75;
			allContentVisible = false;

			// Wait for the DOM to update with the filtered items
			tick().then(() => {
				if (contentSection && containerDiv) {
					const currentContainerHeightPx = containerDiv.offsetHeight;
					const contentScrollHeight = contentSection.scrollHeight;

					if (currentContainerHeightPx < contentScrollHeight) {
						const remainingHiddenPx = contentScrollHeight - currentContainerHeightPx;
						const remainingHiddenSvh = (remainingHiddenPx / window.innerHeight) * 100;

						if (remainingHiddenPx > 0 && remainingHiddenSvh < 20) {
							currentHeightVh = (contentScrollHeight / window.innerHeight) * 100;
							allContentVisible = true;
						} else {
							// Ensure allContentVisible remains false if there's enough content to justify "Load more"
							// No change needed here, as it's already set to false before the tick()
						}
					} else {
						allContentVisible = true;
					}
				}
			});
		}
	});

	let wrapperHeight = $derived(allContentVisible ? 'auto' : `${currentHeightVh}svh`);
</script>

<div bind:this={containerDiv} class="relative overflow-hidden" style="height: {wrapperHeight}">
	<section bind:this={contentSection} use:editable={content} class="py-8 lg:py-12">
		<PageGrid16>
			<div
				class="scrollbar-hide col-span-full mb-8 flex gap-x-3 overflow-x-auto px-5 text-[14px] leading-normal text-brown-1 lg:hidden"
			>
				{#each availableMobileCategories as category}
					<button
						type="button"
						onclick={() => (activeMobileCategory = category as ActiveCategory)}
						class={[
							'shrink-0 basis-20 rounded-[44px] px-5 py-[15px]',
							{
								'border border-brown-5 hover:border-brown-1': activeMobileCategory !== category,
								'border-2 border-brown-1': activeMobileCategory === category
							}
						]}>{categoryNames[category as ActiveCategory]}</button
					>
				{/each}
			</div>
			<div
				class="scrollbar-hide col-span-full mb-12 flex justify-center gap-x-3 overflow-x-auto text-[14px] leading-normal text-brown-1 max-lg:hidden"
			>
				{#each availableDesktopCategories as category}
					<button
						type="button"
						onclick={() => (activeDesktopCategory = category as ActiveCategory)}
						class={[
							'shrink-0 basis-20 rounded-[44px] px-5 py-[15px]',
							{
								'border border-brown-5 hover:border-brown-1': activeDesktopCategory !== category,
								'border-2 border-brown-1': activeDesktopCategory === category
							}
						]}>{categoryNames[category as ActiveCategory]}</button
					>
				{/each}
			</div>
			<div class="col-span-full mx-5 columns-2 gap-3 space-y-3 lg:hidden">
				{#each filteredMobileItems as blok}
					<div class="break-inside-avoid">
						<Blok content={blok} delay={0} threshold={0} />
					</div>
				{/each}
			</div>
			<div class="col-start-1 col-end-[16] mx-5 columns-4 gap-4 space-y-4 max-lg:hidden">
				{#each filteredDesktopItems as blok}
					<div class="break-inside-avoid">
						<Blok content={blok} delay={0} threshold={0} />
					</div>
				{/each}
			</div>
		</PageGrid16>
	</section>
	{#if !allContentVisible}
		<div class="absolute inset-x-0 bottom-0" in:slide={{ duration: 460 }}>
			<div class="load-more-overlay h-40"></div>
			<div class="flex justify-center bg-white pb-12">
				<Button onclick={loadMore} class="btn-brown">Load more</Button>
			</div>
		</div>
	{/if}
</div>

<style>
	.load-more-overlay {
		background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
	}
</style>
